import { Link, useNavigate } from "@tanstack/react-router"
import type { MarketplaceId } from "@nova-ai/shared"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect } from "react"

import { useOrders, type OrderListItem, type OrdersStateFilter } from "../../services/use-orders"
import { cn } from "../../lib/utils"

type OrderNavProps = {
  /** The order currently on screen, identified by its grid coordinates. */
  current: { marketplace: MarketplaceId; orderId: string }
  /** The marketplace + state filter the grid was showing, so prev/next walks
   *  exactly the list the operator was browsing (and the same cached query). */
  marketplaces: MarketplaceId[]
  state: OrdersStateFilter
  query?: string | undefined
}

const buttonClasses =
  "inline-flex h-11 w-11 sm:h-8 sm:w-8 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-card transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"

/** The filter search params forwarded on navigation, so a neighbour's detail
 *  page keeps the same list context. Empties collapse to `undefined` so the URL
 *  stays clean. */
function linkSearchFor(state: OrdersStateFilter, marketplaces: MarketplaceId[], query?: string) {
  return {
    q: query ?? undefined,
    state: state === "all" ? undefined : state,
    marketplace: marketplaces.length > 0 ? marketplaces : undefined,
  }
}

/** Prev/next chevrons in the order detail header. Reads the same orders list the
 *  grid renders from (newest-first) and steps to the neighbouring row, so an
 *  operator can page through orders without bouncing back to the grid. Left/right
 *  arrow keys mirror the chevrons. */
export function OrderNav({ current, marketplaces, state, query }: OrderNavProps) {
  const navigate = useNavigate()
  const orders = useOrders({ marketplaces, state, search: query ?? "" })
  // Walk the loaded pages (newest-first), the same cached query the grid fills.
  const list = (orders.data?.pages ?? []).flatMap((p) => p.orders)

  const index = list.findIndex(
    (o) => o.marketplace === current.marketplace && o.orderId === current.orderId
  )
  const prev = index > 0 ? (list[index - 1] ?? null) : null
  const next = index >= 0 && index < list.length - 1 ? (list[index + 1] ?? null) : null

  const linkSearch = linkSearchFor(state, marketplaces, query)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return
      }
      const target = e.target as HTMLElement | null
      const typing =
        target?.isContentEditable === true ||
        /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? "")
      if (typing) {
        return
      }
      const dest = { ArrowLeft: prev, ArrowRight: next }[e.key] ?? null
      if (!dest) {
        return
      }
      e.preventDefault()
      void navigate({
        to: "/orders/$marketplace/$orderId",
        params: { marketplace: dest.marketplace, orderId: dest.orderId },
        search: linkSearchFor(state, marketplaces, query),
      })
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [prev, next, navigate, state, marketplaces, query])

  return (
    <div className="flex items-center justify-between gap-1 sm:justify-start">
      <ChevronButton target={prev} search={linkSearch} direction="prev" />

      {index >= 0 ? (
        <span className="min-w-12 text-center text-xs tabular-nums text-muted-foreground">
          {`${index + 1} of ${list.length}`}
        </span>
      ) : null}

      <ChevronButton target={next} search={linkSearch} direction="next" />
    </div>
  )
}

function ChevronButton({
  target,
  search,
  direction,
}: {
  target: OrderListItem | null
  search: {
    state: OrdersStateFilter | undefined
    marketplace: MarketplaceId[] | undefined
    q: string | undefined
  }
  direction: "prev" | "next"
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight
  const label = direction === "prev" ? "Previous order (←)" : "Next order (→)"

  if (!target) {
    return (
      <button type="button" disabled aria-label={label} className={buttonClasses}>
        <Icon className="size-4" />
      </button>
    )
  }

  return (
    <Link
      to="/orders/$marketplace/$orderId"
      params={{ marketplace: target.marketplace, orderId: target.orderId }}
      search={search}
      aria-label={label}
      title={label}
      className={cn(buttonClasses)}
    >
      <Icon className="size-4" />
    </Link>
  )
}
